import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from 'prosemirror-state';
import React from 'react';
import ReactDOM from 'react-dom';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import AddCommentIcon from '@mui/icons-material/AddComment';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RateReviewIcon from '@mui/icons-material/RateReview';
import { useTranslation } from 'react-i18next';

const TextSelectionBubblePluginKey = new PluginKey('textSelectionBubble');

/** Vertical pill bubble with actions — positioned to the right of the selection. */
function SelectionBubble({ onComment, onAI, onSuggest, onAISuggest, hideAI, showAISuggest, aiSuggestLoading }) {
  const { t } = useTranslation();
  const anyLoading = aiSuggestLoading;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '2px',
        padding: '6px 4px',
      }}
    >
      <Tooltip title="Commenter" placement="left">
        <span>
          <IconButton
            size="small"
            color="primary"
            onClick={onComment}
            disabled={anyLoading}
            sx={{ p: 0.75 }}
          >
            <AddCommentIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </span>
      </Tooltip>

      {!hideAI && (
        <Tooltip title="AI Assistant (⌘K)" placement="left">
          <span>
            <IconButton
              size="small"
              color="primary"
              onClick={onAI}
              disabled={anyLoading}
              sx={{ p: 0.75 }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      )}

      <Tooltip title={t('propose_modification')} placement="left">
        <span>
          <IconButton
            size="small"
            color="primary"
            onClick={onSuggest}
            disabled={anyLoading}
            sx={{ p: 0.75 }}
          >
            <RateReviewIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </span>
      </Tooltip>

      {showAISuggest && (
        <Tooltip title={t('monitoring.generateSuggestion', 'AI Suggestion')} placement="left">
          <span>
            <IconButton
              size="small"
              onClick={onAISuggest}
              disabled={aiSuggestLoading}
              sx={{ p: 0.75, color: '#c9a96e' }}
            >
              {aiSuggestLoading
                ? <CircularProgress size={18} sx={{ color: '#c9a96e' }} />
                : <AutoAwesomeIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </span>
        </Tooltip>
      )}
    </div>
  );
}

class TextSelectionBubbleView {
  constructor(view, options) {
    this.view = view;
    this.options = options;
    this.selectionCoords = null;
    this.selectionInfo = null; // { from, to, text }
    this.aiSuggestLoading = false;

    this.dom = document.createElement('div');
    this.dom.className = 'text-selection-bubble';
    Object.assign(this.dom.style, {
      position: 'fixed',
      zIndex: '1200',
      background: 'white',
      borderRadius: '24px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
      display: 'none',
      opacity: '0',
      transition: 'opacity 0.15s ease',
    });

    this._renderReact();
    document.body.appendChild(this.dom);

    this.boundUpdatePosition = this.updatePosition.bind(this);
    window.addEventListener('scroll', this.boundUpdatePosition, true);
    window.addEventListener('resize', this.boundUpdatePosition);

    window.textSelectionBubble = this;
  }

  _renderReact() {
    const hideAI = !!this.options.isReviewerRef?.current;
    const alertData = this.options.activeAlertRef?.current;
    const showAISuggest = !!(alertData && alertData.canEdit);
    ReactDOM.render(
      <SelectionBubble
        onComment={() => this._handleComment()}
        onAI={() => this._handleAI()}
        onSuggest={() => this._handleSuggest()}
        onAISuggest={() => this._handleAISuggest()}
        hideAI={hideAI}
        showAISuggest={showAISuggest}
        aiSuggestLoading={this.aiSuggestLoading}
      />,
      this.dom,
    );
  }

  _handleComment() {
    this.hide();
    if (this.selectionInfo && this.options.onCommentClick) {
      this.options.onCommentClick({
        ...this.selectionInfo,
        selectionY: this.selectionCoords?.top,
      });
    }
  }

  _handleAI() {
    this.hide();
    const { aiButtonRef } = this.options;
    if (aiButtonRef?.current) {
      setTimeout(() => {
        if (typeof aiButtonRef.current.openAtSelectionPosition === 'function') {
          aiButtonRef.current.openAtSelectionPosition();
        } else {
          aiButtonRef.current.click();
        }
      }, 50);
    }
  }

  _handleSuggest() {
    this.hide();
    if (this.selectionInfo && this.options.onSuggestClick) {
      this.options.onSuggestClick({
        ...this.selectionInfo,
        selectionY: this.selectionCoords?.top,
      });
    }
  }

  async _handleAISuggest() {
    if (this.aiSuggestLoading) return;
    if (!this.selectionInfo || !this.options.onAISuggestClick) return;
    const startedFor = this.selectionInfo;
    this.aiSuggestLoading = true;
    this._renderReact();
    try {
      await this.options.onAISuggestClick({
        ...startedFor,
        selectionY: this.selectionCoords?.top,
      });
    } finally {
      // Only clear/hide if the user hasn't moved to a different selection mid-await
      if (this.selectionInfo === startedFor) {
        this.aiSuggestLoading = false;
        this._renderReact();
        this.hide();
      }
    }
  }

  _getSelectionInfo(view) {
    const { from, to } = view.state.selection;
    if (from === to) return null;
    const text = view.state.doc.textBetween(from, to, ' ');
    return { from, to, text };
  }

  updatePosition() {
    if (!this.selectionCoords || this.dom.style.display === 'none') return;
    const { from, to } = this.view.state.selection;
    if (from === to) return;

    try {
      const coords = this._computeCoords(this.view, from, to);
      if (!coords) return;
      this.selectionCoords = coords;
      this._applyPosition(coords);
    } catch (e) {
      this.hide();
    }
  }



  _computeCoords(view, from, to) {
    const start = view.coordsAtPos(from);
    const end = view.coordsAtPos(to);

    // Vertical midpoint of the selection.
    const top = (start.top + end.bottom) / 2;

    // Right edge of the editor DOM — use this to anchor the bubble inside the editor.
    const editorRight = view.dom.getBoundingClientRect().right;

    return { top, editorRight };
  }

  _applyPosition({ top, editorRight }) {
    // Place the bubble just outside the right edge of the editor.
    this.dom.style.right = '';
    this.dom.style.left = `${editorRight + 8}px`;
    this.dom.style.top = `${top - this.dom.offsetHeight / 2}px`;
    this.dom.style.opacity = '1';
  }

  update(view, lastState) {
    this.view = view;
    const { from, to } = view.state.selection;

    if (from === to) {
      this.hide();
      this.selectionCoords = null;
      this.selectionInfo = null;
      return;
    }

    const sameSelection = lastState && lastState.selection.eq(view.state.selection);
    if (!sameSelection) {
      try {
        const coords = this._computeCoords(view, from, to);
        if (!coords) return;

        // User moved to a different selection — any in-flight AI loading state
        // belonged to the previous selection, so clear it for the new bubble.
        if (this.aiSuggestLoading) this.aiSuggestLoading = false;

        this.selectionCoords = coords;
        this.selectionInfo = this._getSelectionInfo(view);

        // Re-render React to pick up latest ref values (e.g., activeAlertRef)
        this._renderReact();

        // When suggest mode is active (non-reviewer), hide bubble — auto-trigger handles it
        // Reviewers: show bubble so they can pick between comment and suggest
        if (this.options.suggestModeRef?.current && !this.options.isReviewerRef?.current) {
          this.hide();
          return;
        }

        // When contract is locked (pending signature / signed), hide bubble
        if (this.options.lockedRef?.current) {
          this.hide();
          return;
        }

        this.dom.style.display = 'block';
        this.dom.style.right = '';
        this.dom.style.left = `${coords.editorRight + 8}px`;
        this.dom.style.top = `${coords.top - (this.dom.offsetHeight || 90) / 2}px`;

        requestAnimationFrame(() => {
          this.dom.style.opacity = '1';
        });
      } catch (e) {
        this.hide();
      }
    }
  }

  hide() {
    this.dom.style.opacity = '0';
    setTimeout(() => {
      if (this.dom.style.opacity === '0') {
        this.dom.style.display = 'none';
      }
    }, 160);
  }

  destroy() {
    window.removeEventListener('scroll', this.boundUpdatePosition, true);
    window.removeEventListener('resize', this.boundUpdatePosition);
    if (this.dom) {
      ReactDOM.unmountComponentAtNode(this.dom);
      this.dom.remove();
      if (window.textSelectionBubble === this) delete window.textSelectionBubble;
    }
  }
}

export const TextSelectionBubble = Extension.create({
  name: 'textSelectionBubble',

  addOptions() {
    return {
      aiButtonRef: null,
      suggestModeRef: null,  // { current: bool } — when true, selection auto-triggers suggest
      lockedRef: null,       // { current: bool } — when true, contract is locked (pending signature / signed)
      isReviewerRef: null,   // { current: bool } — when true, show only comment + suggest (no AI)
      activeAlertRef: null,  // { current: object|null } — when set, show AI suggest button (gold)
      onCommentClick: null,  // ({ from, to, text, selectionY }) => void
      onSuggestClick: null,  // ({ from, to, text, selectionY }) => void
      onAISuggestClick: null, // ({ from, to, text, selectionY }) => void
    };
  },

  addProseMirrorPlugins() {
    const options = this.options;

    return [
      new Plugin({
        key: TextSelectionBubblePluginKey,
        view: (editorView) => new TextSelectionBubbleView(editorView, options),
      }),
    ];
  },
});

export function hideTextSelectionBubbles() {
  if (window.textSelectionBubble) {
    window.textSelectionBubble.hide();
  }
}
